/**
 * Disbursement Trend Chart - Smooth curve with proper labels
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { api } from '@/lib/api';
import { formatCurrency, formatCurrencyCompact } from '@/lib/formatters';
import { Link } from 'react-router-dom';

export function DisbursementTrendChart() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/dashboard/disbursement-chart?months=6')
      .then((response) => {
        setData(response);
      })
      .catch(() => {
        setData([]);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Disbursement Trend</CardTitle>
          <Link to="/disbursements" className="text-sm text-primary hover:underline">
            View all
          </Link>
        </CardHeader>
        <CardContent>
          <div className="h-[250px] flex items-center justify-center text-muted-foreground">
            Loading...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Disbursement Trend</CardTitle>
          <Link to="/disbursements" className="text-sm text-primary hover:underline">
            View all
          </Link>
        </CardHeader>
        <CardContent>
          <div className="h-[250px] flex items-center justify-center text-muted-foreground">
            No disbursement data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const maxValue = Math.max(...data.map(d => d.amount)) * 1.2 || 100;
  const chartHeight = 250;
  const chartWidth = 700;
  const paddingLeft = 70;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 40;

  const getX = (index) => paddingLeft + (index / Math.max(data.length - 1, 1)) * (chartWidth - paddingLeft - paddingRight);
  const getY = (value) => paddingTop + ((maxValue - value) / maxValue) * (chartHeight - paddingTop - paddingBottom);

  // Create smooth curve path using cubic bezier curves
  const createSmoothPath = () => {
    if (data.length === 0) return '';
    if (data.length === 1) {
      const x = getX(0);
      const y = getY(data[0].amount);
      return `M ${x} ${y}`;
    }

    let path = '';
    for (let i = 0; i < data.length; i++) {
      const x = getX(i);
      const y = getY(data[i].amount);

      if (i === 0) {
        path += `M ${x} ${y}`;
      } else {
        // Calculate control points for smooth curve
        const prevX = getX(i - 1);
        const prevY = getY(data[i - 1].amount);
        const controlPoint1X = prevX + (x - prevX) / 2;
        const controlPoint1Y = prevY;
        const controlPoint2X = prevX + (x - prevX) / 2;
        const controlPoint2Y = y;
        
        path += ` C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${x} ${y}`;
      }
    }
    return path;
  };

  const createSmoothAreaPath = () => {
    if (data.length === 0) return '';
    const linePath = createSmoothPath();
    const lastX = getX(data.length - 1);
    const firstX = getX(0);
    const bottomY = chartHeight - paddingBottom;
    return `${linePath} L ${lastX} ${bottomY} L ${firstX} ${bottomY} Z`;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold">Disbursement Trend</CardTitle>
        <Link to="/disbursements" className="text-sm text-primary hover:underline">
          View all
        </Link>
      </CardHeader>
      <CardContent>
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full">
          <defs>
            <linearGradient id="gradient-disbursement" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          {/* Horizontal grid lines with Y-axis labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const value = maxValue * ratio;
            const y = getY(value);
            return (
              <g key={ratio}>
                <line
                  x1={paddingLeft}
                  y1={y}
                  x2={chartWidth - paddingRight}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  strokeDasharray="3,3"
                />
                <text
                  x={paddingLeft - 10}
                  y={y}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  fill="#6b7280"
                  fontSize="11"
                  fontFamily="'IBM Plex Sans', system-ui, sans-serif"
                >
                  {formatCurrencyCompact(value)}
                </text>
              </g>
            );
          })}

          {/* X-axis labels (months) */}
          {data.map((item, index) => (
            <text
              key={index}
              x={getX(index)}
              y={chartHeight - paddingBottom + 25}
              textAnchor="middle"
              fill="#6b7280"
              fontSize="11"
              fontFamily="'IBM Plex Sans', system-ui, sans-serif"
            >
              {item.month}
            </text>
          ))}

          {/* Area under line */}
          <path
            d={createSmoothAreaPath()}
            fill="url(#gradient-disbursement)"
          />

          {/* Smooth curve line */}
          <path
            d={createSmoothPath()}
            fill="none"
            stroke="#10b981"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points with hover tooltip */}
          {data.map((item, index) => (
            <g key={index}>
              <circle
                cx={getX(index)}
                cy={getY(item.amount)}
                r="5"
                fill="#10b981"
                stroke="white"
                strokeWidth="2"
                className="cursor-pointer"
              >
                <title>{`${item.month}: ${formatCurrency(item.amount)}`}</title>
              </circle>
            </g>
          ))}
        </svg>
      </CardContent>
    </Card>
  );
}
